import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { BRAND } from '../brand';

const API = process.env.REACT_APP_BACKEND_URL;

const Register = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [rutPassport, setRutPassport] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  // Email verification states
  const [step, setStep] = useState('form'); // 'form' | 'verify'
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [codeSent, setCodeSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const inputRefs = useRef([]);

  // Countdown timer for resend
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const formatRut = (value) => {
    let rut = value.replace(/[^0-9kK]/g, '').toUpperCase();
    if (rut.length > 1) {
      const dv = rut.slice(-1);
      let numero = rut.slice(0, -1);
      numero = numero.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return `${numero}-${dv}`;
    }
    return rut;
  };

  const handleRutChange = (e) => {
    const value = e.target.value;
    if (/^[0-9]/.test(value.replace(/[^0-9kK]/g, ''))) {
      setRutPassport(formatRut(value));
    } else {
      setRutPassport(value.toUpperCase());
    }
  };

  // Send verification code
  const handleSendCode = async () => {
    setError('');
    
    // Validate form first
    const nameParts = fullName.trim().split(/\s+/);
    if (nameParts.length < 2) {
      setError('Debe ingresar nombre y apellido completos');
      return;
    }
    if (rutPassport.length < 6) {
      setError('Debe ingresar un RUT o pasaporte válido');
      return;
    }
    // Validar RUT chileno (si parece RUT chileno por formato)
    try {
      const { looksLikeChileanRut, isValidRut } = await import('../utils/rutValidator');
      if (looksLikeChileanRut(rutPassport) && !isValidRut(rutPassport)) {
        setError('El RUT ingresado no es válido. Verifica el número y dígito verificador.');
        return;
      }
    } catch (_) { /* fallback: backend valida igualmente */ }

    // Validar WhatsApp: solo dígitos y +, mínimo 8 dígitos. Sin letras ni símbolos.
    if (whatsapp) {
      const waDigits = whatsapp.replace(/\D/g, '');
      if (/[a-zA-Z]/.test(whatsapp)) {
        setError('El teléfono no puede contener letras. Solo dígitos y el signo +.');
        return;
      }
      if (waDigits.length < 8) {
        setError('El teléfono debe tener al menos 8 dígitos.');
        return;
      }
      if (waDigits.length > 15) {
        setError('El teléfono es demasiado largo. Verifica el código de país.');
        return;
      }
    }
    if (!email || !email.includes('@')) {
      setError('Ingresa un correo válido');
      return;
    }
    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setLoading(true);
    try {
      const resp = await axios.post(`${API}/api/auth/send-verification-code`, { email: email.trim().toLowerCase() });
      // BYPASS: si la verificacion esta desactivada, registramos directamente
      if (resp.data?.bypass) {
        try {
          await register(email, password, fullName, rutPassport, whatsapp);
          navigate('/dashboard');
          return;
        } catch (regErr) {
          setError(regErr?.response?.data?.detail || 'Error al registrarse');
          return;
        }
      }
      setStep('verify');
      setCodeSent(true);
      setCountdown(60);
      setVerificationCode(['', '', '', '', '', '']);
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (detail === 'EMAIL_ALREADY_REGISTERED') {
        setError('Este correo ya está registrado. ¿Quieres iniciar sesión?');
      } else {
        setError(detail || 'Error al enviar el código');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle code input (6 boxes)
  const handleCodeChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...verificationCode];
    newCode[index] = value.slice(-1);
    setVerificationCode(newCode);
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleCodePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setVerificationCode(pasted.split(''));
      inputRefs.current[5]?.focus();
    }
  };

  // Verify code and complete registration
  const handleVerifyAndRegister = async () => {
    const code = verificationCode.join('');
    if (code.length !== 6) {
      setError('Ingresa el código completo de 6 dígitos');
      return;
    }

    setError('');
    setLoading(true);
    try {
      // Verify the code
      await axios.post(`${API}/api/auth/verify-email-code`, {
        email: email.trim().toLowerCase(),
        code
      });

      // Code verified! Now register
      await register(email, password, fullName, rutPassport, whatsapp);
      navigate('/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (detail === 'EMAIL_NOT_VERIFIED') {
        setError('El código no fue verificado. Intenta de nuevo.');
      } else {
        setError(detail || 'Error al verificar el código');
      }
    } finally {
      setLoading(false);
    }
  };

  // Resend code
  const handleResendCode = async () => {
    if (countdown > 0) return;
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/send-verification-code`, { email: email.trim().toLowerCase() });
      setCountdown(60);
      setVerificationCode(['', '', '', '', '', '']);
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al reenviar el código');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex flex-col">
      <nav className="bg-white border-b-2 border-green-200 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <Link to="/" className="flex items-center">
            <img src="/logo.png" alt={`${BRAND.name}`} className="h-12" />
          </Link>
        </div>
      </nav>

      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full">
          {/* Barra de progreso onboarding */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2 text-xs">
              <span className="font-bold text-green-800">
                Paso {step === 'form' ? '1' : '2'} de 5
              </span>
              <span className="text-gray-500">
                {step === 'form' ? 'Te faltan ~4 minutos' : 'Te faltan ~3 minutos'}
              </span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-green-500 to-emerald-600 transition-all duration-500"
                style={{ width: step === 'form' ? '20%' : '40%' }}
                data-testid="register-progress-bar"
              />
            </div>
            <div className="flex justify-between mt-1 text-[10px] text-gray-400">
              <span className={step === 'form' ? 'font-bold text-green-700' : ''}>Datos</span>
              <span className={step === 'verify' ? 'font-bold text-green-700' : ''}>Email</span>
              <span>Cédula</span>
              <span>Selfie</span>
              <span>Listo</span>
            </div>
          </div>

          <div className="text-center mb-8">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2" data-testid="register-title">
              {step === 'form' ? 'Crea tu cuenta segura' : 'Verifica que eres tú'}
            </h1>
            <p className="text-gray-600 text-sm sm:text-base">
              {step === 'form'
                ? 'Gratis y en 5 minutos. Tus datos están protegidos y nunca los compartimos con terceros.'
                : `Te enviamos un código a ${email} para confirmar que eres tú — nadie más puede acceder a tu cuenta.`}
            </p>
          </div>
          
          <div className="bg-white rounded-3xl p-8 border-2 border-green-200 shadow-2xl">
            {error && (
              <div className="bg-red-50 border-2 border-red-300 text-red-700 px-4 py-3 rounded-xl mb-6" data-testid="register-error">
                {error}
                {error.includes('iniciar sesión') && (
                  <Link to="/login" className="block mt-2 text-green-700 font-bold underline">
                    Ir a iniciar sesión
                  </Link>
                )}
              </div>
            )}

            {step === 'form' ? (
              <>
                <form onSubmit={(e) => { e.preventDefault(); handleSendCode(); }} data-testid="register-form">
                  <div className="mb-5">
                    <label className="block text-green-800 text-sm font-bold mb-2" htmlFor="fullName">
                      Nombre y Apellidos *
                    </label>
                    <input
                      type="text"
                      id="fullName"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className="w-full bg-blue-50 border-2 border-blue-300 rounded-xl px-4 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      placeholder="Juan Antonio Pérez González"
                      required
                      data-testid="fullname-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">Ingresa tu nombre completo como aparece en tu documento</p>
                  </div>

                  <div className="mb-5">
                    <label className="block text-green-800 text-sm font-bold mb-2" htmlFor="rutPassport">
                      RUT o Pasaporte *
                    </label>
                    <input
                      type="text"
                      id="rutPassport"
                      value={rutPassport}
                      onChange={handleRutChange}
                      className="w-full bg-blue-50 border-2 border-blue-300 rounded-xl px-4 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      placeholder="12.345.678-9 o AB123456"
                      required
                      minLength="6"
                      data-testid="rut-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">RUT chileno con puntos y guión, o número de pasaporte</p>
                  </div>

                  <div className="mb-5">
                    <label className="block text-green-800 text-sm font-bold mb-2" htmlFor="email">
                      Correo electrónico *
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full bg-blue-50 border-2 border-blue-300 rounded-xl px-4 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      placeholder="tu@email.com"
                      required
                      data-testid="email-input"
                    />
                  </div>

                  <div className="mb-5">
                    <label className="block text-green-800 text-sm font-bold mb-2" htmlFor="whatsapp">
                      WhatsApp
                    </label>
                    <input
                      type="tel"
                      id="whatsapp"
                      value={whatsapp}
                      onChange={(e) => {
                        // Auto-sanitizar: solo dígitos y + al inicio
                        const raw = e.target.value;
                        const startsWithPlus = raw.trim().startsWith('+');
                        const digits = raw.replace(/\D/g, '');
                        setWhatsapp(startsWithPlus ? '+' + digits : digits);
                      }}
                      className="w-full bg-blue-50 border-2 border-blue-300 rounded-xl px-4 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      placeholder="+56912345678"
                      pattern="\+?[0-9]{8,15}"
                      inputMode="tel"
                      maxLength={16}
                      required
                      data-testid="whatsapp-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">Incluye código de país: +56 o +591</p>
                  </div>

                  <div className="mb-6">
                    <label className="block text-green-800 text-sm font-bold mb-2" htmlFor="password">
                      Contraseña
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        id="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full bg-blue-50 border-2 border-blue-300 rounded-xl px-4 py-3 pr-12 text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        placeholder="••••••••"
                        required
                        minLength="6"
                        data-testid="password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                        data-testid="toggle-password"
                      >
                        {showPassword ? <span className="text-xl">👁️</span> : <span className="text-xl">👁️‍🗨️</span>}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Mínimo 6 caracteres</p>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-blue-500 via-blue-600 to-blue-500 hover:from-blue-600 hover:via-blue-700 hover:to-blue-600 text-white font-bold py-3 px-4 rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02] shadow-lg"
                    data-testid="register-submit-button"
                  >
                    {loading ? 'Enviando código...' : 'Verificar Correo'}
                  </button>
                </form>
              </>
            ) : (
              <div data-testid="verification-step">
                <div className="text-center mb-6">
                  <div className="text-5xl mb-3">📧</div>
                  <p className="text-gray-700 text-sm">
                    Ingresa el código de <strong>6 dígitos</strong> que enviamos a:
                  </p>
                  <p className="font-bold text-green-700 mt-1">{email}</p>
                </div>

                {/* 6-digit code input */}
                <div className="flex justify-center gap-2 mb-6" onPaste={handleCodePaste}>
                  {verificationCode.map((digit, i) => (
                    <input
                      key={i}
                      ref={(el) => (inputRefs.current[i] = el)}
                      type="tel"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleCodeChange(i, e.target.value)}
                      onKeyDown={(e) => handleCodeKeyDown(i, e)}
                      className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-xl focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none transition"
                      data-testid={`code-input-${i}`}
                    />
                  ))}
                </div>

                <button
                  onClick={handleVerifyAndRegister}
                  disabled={loading || verificationCode.join('').length !== 6}
                  className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-bold py-3 px-4 rounded-xl transition disabled:opacity-50 shadow-lg mb-4"
                  data-testid="verify-code-button"
                >
                  {loading ? 'Verificando...' : 'Verificar y Crear Cuenta'}
                </button>

                <div className="text-center">
                  {countdown > 0 ? (
                    <p className="text-gray-500 text-sm">
                      Reenviar código en <strong>{countdown}s</strong>
                    </p>
                  ) : (
                    <button
                      onClick={handleResendCode}
                      disabled={loading}
                      className="text-green-700 font-bold text-sm hover:underline"
                      data-testid="resend-code-button"
                    >
                      Reenviar código
                    </button>
                  )}
                </div>

                <button
                  onClick={() => { setStep('form'); setError(''); }}
                  className="w-full mt-4 text-gray-500 text-sm hover:text-gray-700"
                >
                  ← Volver al formulario
                </button>
              </div>
            )}

            <div className="mt-6 text-center">
              <p className="text-gray-600">
                ¿Ya tienes cuenta?{' '}
                <Link to="/login" className="text-green-700 hover:text-green-800 font-bold" data-testid="login-link">
                  Inicia sesión
                </Link>
              </p>
            </div>
          </div>

          <div className="text-center mt-6">
            <Link to="/" className="text-gray-600 hover:text-gray-800 text-sm">
              ← Volver al inicio
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
